// ============================================================================
// contextChat.ts — SSoT бейджей «обсуждение» (непрочитанные по контекстам):
//   - unreadByContext: key `${contextType}:${contextId}` → счётчик
//     (все чаты с контекстом из ListConversations — один запрос);
//   - convContext: convId → {type, id} (для точечного +1 по событию);
//   - событийная модель (eventBusHub): chat.message.created.* → +1 (чужое),
//     chat.conversation.updated → полное обновление; НИКАКИХ таймеров-поллинга.
// Используется: иконка чата в шапке объекта (AutoObjectForm), карточки
// списков (AutoListForm → ObjectCard unreadCount).
// ============================================================================

import { create } from 'zustand';
import { getSDK } from '@/store/sdkStore';
import { eventBusHub } from '@/lib/eventBusHub';

export function contextKey(type: string, id: string): string {
  return `${type}:${id}`;
}

interface ContextChatState {
  /** key `${contextType}:${contextId}` → непрочитанные */
  unreadByContext: Record<string, number>;
  /** convId → {type, id} (для точечного обновления по событиям) */
  convContext: Record<string, { type: string; id: string }>;
  /** id текущего пользователя (устанавливается при входе) */
  myId: string;
  loaded: boolean;
  setMyId: (id: string) => void;
  refresh: () => Promise<void>;
  /** Обнулить непрочитанные конкретного контекста (после markConversationRead) */
  markRead: (type: string, id: string, conversationId: string) => void;
  unreadFor: (type: string, id: string) => number;
}

let initialized = false;

export const useContextChatStore = create<ContextChatState>((set, get) => ({
  unreadByContext: {},
  convContext: {},
  myId: '',
  loaded: false,

  setMyId: (id) => set({ myId: id }),

  refresh: async () => {
    try {
      const r: any = await getSDK().messenger.listConversations({});
      const unreadByContext: Record<string, number> = {};
      const convContext: Record<string, { type: string; id: string }> = {};
      for (const c of (r?.items || []) as any[]) {
        const type = String(c.contextType || '');
        const id = String(c.contextId || '');
        if (!type || !id) continue;
        convContext[c.id] = { type, id };
        const key = contextKey(type, id);
        unreadByContext[key] = (unreadByContext[key] || 0) + Number(c.unreadCount || 0);
      }
      set({ unreadByContext, convContext, loaded: true });
    } catch {
      // Нет доступа к списку чатов (не авторизован) — бейджи остаются пустыми
    }
  },

  markRead: (type, id, conversationId) => {
    const key = contextKey(type, id);
    const cur = get().unreadByContext[key] || 0;
    if (cur === 0) return;
    set((s) => ({
      unreadByContext: { ...s.unreadByContext, [key]: 0 },
      convContext: { ...s.convContext, [conversationId]: { type, id } },
    }));
  },

  unreadFor: (type, id) => get().unreadByContext[contextKey(type, id)] || 0,
}));

/** Инициализация бейджей: подписки на события (refresh по conversation.updated;
 * первичное обновление — при авторизации через setMyId). Вызывается в main.tsx. */
export function initContextChat(): void {
  if (initialized) return;
  initialized = true;
  const store = useContextChatStore;
  eventBusHub.subscribe((ev) => {
    if (ev.topic.startsWith('chat.message.created.')) {
      const convId = ev.topic.slice('chat.message.created.'.length);
      const senderId = String(ev.payload?.sender_id || '');
      const ctx = store.getState().convContext[convId];
      if (!ctx || senderId === store.getState().myId) return;
      const key = contextKey(ctx.type, ctx.id);
      store.setState((s) => ({
        unreadByContext: { ...s.unreadByContext, [key]: (s.unreadByContext[key] || 0) + 1 },
      }));
    } else if (ev.topic.startsWith('chat.message.read.')) {
      // Прочитано МНОЙ (user_id в payload): обнуляем бейдж контекста —
      // иначе после чтения из панели бейдж висит
      const convId = ev.topic.slice('chat.message.read.'.length);
      if (String(ev.payload?.user_id || '') !== store.getState().myId) return;
      const ctx = store.getState().convContext[convId];
      if (ctx) store.getState().markRead(ctx.type, ctx.id, convId);
    } else if (ev.topic === 'chat.conversation.updated') {
      void store.getState().refresh();
    }
  });
}
